/**
 * PRD.330 Phase 3 — Clinic Collection Details: report-specific wiring
 * (pure, React-free so it can be unit-tested).
 *
 * Connects the Phase 1 definition + query builders to the Phase 2 executor.
 * The bindings are MODULE-LEVEL constants so `useReportQuery` sees stable
 * identities and only recompiles when the applied query changes.
 */
import { CLINIC_COLLECTIONS_REPORT, paymentMethodLabel } from '@helios/domain';
import type { ClinicCollectionsColumnId } from '@helios/domain';
import {
  qClinicCollectionsByMethod,
  qClinicCollectionsMethodFacet,
  qClinicCollectionsOverTime,
  qClinicCollectionsRows,
  qClinicCollectionsSummary,
  type ClinicCollectionRow,
} from '@helios/data-access';
import type { ReportQueryBindings } from '../../hooks/useReportQuery';

export const CLINIC_COLLECTIONS_DEFINITION = CLINIC_COLLECTIONS_REPORT;

/** Rows / summary (count + Total Collection Amount) / Payment Method facet. */
export const CLINIC_COLLECTIONS_BINDINGS: ReportQueryBindings<ClinicCollectionsColumnId> = {
  rows: qClinicCollectionsRows,
  summary: qClinicCollectionsSummary,
  facets: { method: qClinicCollectionsMethodFacet },
  // PRD.330 Phase 4 — module-level, like the other bindings (stable identity).
  visualizations: {
    collectionsByMethod: qClinicCollectionsByMethod,
    collectionsOverTime: qClinicCollectionsOverTime,
  },
};

/** The report's visualizations, taken (and narrowed by kind) from the Phase 1 definition. */
function visualization<K extends 'categoryBar' | 'timeSeries'>(id: string, kind: K) {
  const v = CLINIC_COLLECTIONS_REPORT.visualizations?.find((x) => x.id === id);
  if (!v || v.kind !== kind) throw new Error(`Missing ${kind} visualization "${id}"`);
  return v as Extract<NonNullable<typeof v>, { kind: K }>;
}
export const COLLECTIONS_BY_METHOD = visualization('collectionsByMethod', 'categoryBar');
export const COLLECTIONS_OVER_TIME = visualization('collectionsOverTime', 'timeSeries');

/** Facet option labels come from the shared payment-method helper only. */
export function clinicCollectionsFacetLabel(
  columnId: ClinicCollectionsColumnId,
  value: string,
): string {
  return columnId === 'method' ? paymentMethodLabel(value) : value;
}

export const CLINIC_COLLECTIONS_TEXT = {
  title: 'Clinic Collection Details',
  subtitle: 'Payments recorded for this organization, by payment date.',
  searchPlaceholder: 'Search patient, invoice #, payment notes…',
  countLabel: 'payments',
  aggregateLabels: {
    amountSum: 'Total Collection Amount',
    averagePayment: 'Average Payment',
  } as Readonly<Record<string, string>>,
  // PRD.330 §17 — required informational hint (decision 2026-09-26).
  summaryNote:
    'Totals use Payment Date. Home "Collections Today" currently counts payments by the date they were recorded, so the two can differ.',
  visualization: {
    columnLabels: { category: 'Payment Method', primary: 'Amount', secondary: 'Payments' },
    emptyMessage: 'No collections for the selected filters and date range.',
  },
  messages: {
    loading: 'Loading collections…',
    noResults: 'No payments match the current search and filters.',
    error: "Couldn't load collections. Try again or check Sync Status.",
  },
} as const;

/** Only the count and the amount sum are shown as summary items. */
export const SUMMARY_AGGREGATE_KEYS = ['amountSum'] as const;

export function emptyMessage(from: string, to: string): string {
  return from === to
    ? `No collections recorded on ${formatLocalDate(from)}.`
    : `No collections recorded between ${formatLocalDate(from)} and ${formatLocalDate(to)}.`;
}

// ─── Display formatting (never fed back into query semantics) ───

const DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/** 'YYYY-MM-DD' (local calendar date) → e.g. "Sep 24, 2026". */
export function formatLocalDate(localDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!m) return localDate;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(
    'en-PH',
    DATE_FORMAT,
  );
}

/**
 * Stored `paid_at` → local calendar date for display. Handles both observed
 * representations (local 'YYYY-MM-DDTHH:mm:ss.sssZ' and PowerSync-replicated
 * 'YYYY-MM-DD HH:mm:ss[.fff]Z'); the space form is normalized first because
 * `Date` parsing of it is not portable. Date only — the dialogs anchor
 * `paid_at` at local noon, so the time is not meaningful.
 */
export function formatPaymentDate(paidAt: string | null | undefined): string {
  if (!paidAt) return '—';
  const d = new Date(paidAt.trim().replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-PH', DATE_FORMAT);
}

/** Cell text for the non-money columns (money uses the regional formatter). */
export function clinicCollectionCellText(
  row: ClinicCollectionRow,
  columnId: ClinicCollectionsColumnId,
): string {
  switch (columnId) {
    case 'paymentDate':
      return formatPaymentDate(row.paid_at);
    case 'patient':
      return row.patient_name?.trim() || '—';
    case 'invoiceNo':
      return row.invoice_no?.trim() || '—';
    case 'method':
      return paymentMethodLabel(row.method);
    case 'notes':
      return row.notes?.trim() || '—';
    case 'amount':
      return String(row.amount_cents);
  }
}
